import {Box, P} from 'exsportia-components'
import {FC} from 'react'

import {PurchaseLimit, SubscriptionActivity} from '../../../api/subscriptions/types'
import {formatPurchaseLimit, getPassHoursGroups} from '../../../helpers/pass-hours'
import {usePassHoursOptions} from '../../../helpers/use-pass-hours-options'
import {TitledSection} from '../../../ui/components/titeled-section/titled-section'
import {useLocale} from '../../../utils/i18n'

type Props = {
  activities?: SubscriptionActivity[]
  purchaseLimit?: PurchaseLimit | null
}

const Row: FC<{label: string; value: string; hint?: string}> = ({label, value, hint}) => (
  <Box py={'4px'}>
    <P color={'text-secondary'} fontSize={'tag'}>
      {hint ? `${label} · ${hint}` : label}
    </P>
    <P fontSize={'body'}>{value}</P>
  </Box>
)

/**
 * Shared by the buy page and the owned-pass page: the days/hours the pass can be used and the
 * per-person purchase limit. Renders nothing when neither is set, so ordinary passes look as before.
 */
export const PassAvailability: FC<Props> = ({activities, purchaseLimit}) => {
  const {l} = useLocale()
  const options = usePassHoursOptions()
  const hoursGroups = getPassHoursGroups(activities, options)
  const limitText = formatPurchaseLimit(purchaseLimit, (key, values) => l(key, values))
  if (!hoursGroups.length && !limitText) {
    return null
  }
  return (
    <TitledSection title={l('pass_availability_title')}>
      <Box>
        {hoursGroups.map((group) => {
          const names = group.activities.flatMap((activity) => activity.entities.map((entity) => entity.name))
          // Only name the activity when the pass has different hours for different activities.
          const hint =
            hoursGroups.length > 1 && names.length
              ? names.length > 1
                ? `${names[0]} +${names.length - 1} ${l('other')}`
                : names[0]
              : undefined
          return (
            <Row
              key={group.text}
              label={l('pass_usable_on')}
              value={group.text}
              hint={hint}
            />
          )
        })}
        {limitText && (
          <Row
            label={l('pass_purchase_limit')}
            value={limitText}
          />
        )}
      </Box>
    </TitledSection>
  )
}
